import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchExchangeRates, getCachedExchangeRates, getExchangeRateSettings, type ExchangeRates } from '@/lib/exchangeRateService';

function formatTRY(amount: number): string {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function calcChange(current: number, prev: number | undefined): number | null {
  if (!prev || prev === 0) return null;
  return Math.round(((current - prev) / prev) * 10000) / 100;
}

interface CurrencyDef {
  code: string;
  name: string;
  key: keyof ExchangeRates;
}

const CURRENCIES: CurrencyDef[] = [
  { code: 'USD', name: 'AMERİKAN DOLARI', key: 'usd_try' },
  { code: 'EUR', name: 'EURO', key: 'eur_try' },
  { code: 'GBP', name: 'İNGİLİZ STERLİNİ', key: 'gbp_try' },
  { code: 'XAU', name: 'GRAM ALTIN', key: 'gold_try' },
];

function RateBar({ currency, rates }: { currency: CurrencyDef; rates: ExchangeRates | null }) {
  if (!rates) return null;

  const value = rates[currency.key] as number | null;
  if (value === null) return null;

  const prevKey = `prev_${currency.key}` as keyof ExchangeRates;
  const prev = rates[prevKey] as number | undefined;
  const change = calcChange(value, prev);
  const isPositive = change !== null && change >= 0;

  return (
    <div className="flex items-center gap-3 px-4 py-3 min-w-[200px] sm:min-w-[220px] border-r border-background-200 last:border-r-0">
      <div className="w-1 h-8 rounded-full bg-emerald-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-medium text-foreground-500 tracking-wide uppercase truncate">
          {currency.name}
        </p>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className="text-xs font-bold text-foreground-700 tabular-nums">
            {currency.code}
          </span>
          <span className="text-sm font-bold text-foreground-900 tabular-nums">
            {formatTRY(value)}
          </span>
        </div>
      </div>
      {change !== null && (
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <i className={`text-xs ${isPositive ? 'text-emerald-500 ri-arrow-up-line' : 'text-red-500 ri-arrow-down-line'}`} />
          <span className={`text-[11px] font-semibold tabular-nums ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
            {isPositive ? '+' : ''}{change}%
          </span>
        </div>
      )}
    </div>
  );
}

function SkeletonBar() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 min-w-[200px] sm:min-w-[220px] border-r border-background-200">
      <div className="w-1 h-8 rounded-full bg-background-200 flex-shrink-0 animate-pulse" />
      <div className="flex-1 min-w-0">
        <div className="h-2.5 bg-background-200 rounded w-20 mb-1.5 animate-pulse" />
        <div className="flex items-center gap-2">
          <div className="h-3 bg-background-200 rounded w-8 animate-pulse" />
          <div className="h-3 bg-background-200 rounded w-16 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

export default function ExchangeRateCards() {
  const [rates, setRates] = useState<ExchangeRates | null>(getCachedExchangeRates());
  const [loading, setLoading] = useState(!rates);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(rates?.updated_at || null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadRates = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const result = await fetchExchangeRates();
      if (result.rates) {
        setRates(result.rates);
        setFromCache(result.fromCache);
        setLastUpdated(result.rates.updated_at);
      }
      if (result.error && !result.rates) {
        setError(result.error);
      }
    } catch {
      setError('Döviz kurları şu anda alınamadı.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRates(false);

    const settings = getExchangeRateSettings();
    if (settings.auto_refresh) {
      const intervalMs = settings.refresh_interval * 60 * 1000;
      intervalRef.current = setInterval(() => {
        loadRates(false);
      }, intervalMs);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [loadRates]);

  const handleRefresh = () => {
    loadRates(true);
  };

  const hasNoData = !rates && !loading;

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-background-200">
        <div className="flex items-center gap-2">
          <i className="ri-exchange-dollar-line text-sm text-foreground-500" />
          <h3 className="text-xs font-semibold text-foreground-700 tracking-wide uppercase">Canlı Döviz Kurları</h3>
          {fromCache && rates && (
            <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full whitespace-nowrap">önbellek</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && rates && !loading && (
            <span className="text-[10px] text-foreground-400 hidden sm:inline">
              {formatTime(lastUpdated)}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="w-7 h-7 flex items-center justify-center rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Horizontal bar */}
      {loading && !rates && (
        <div className="flex items-stretch overflow-x-auto scrollbar-thin">
          <SkeletonBar />
          <SkeletonBar />
          <SkeletonBar />
          <SkeletonBar />
        </div>
      )}

      {!loading && error && !rates && (
        <div className="flex items-center justify-center gap-3 px-4 py-4">
          <i className="ri-error-warning-line text-foreground-400 text-sm" />
          <span className="text-xs text-foreground-500">Kur bilgisi alınamadı</span>
          <button
            onClick={handleRefresh}
            className="text-xs text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
          >
            Yeniden Dene
          </button>
        </div>
      )}

      {hasNoData && !error && (
        <div className="flex items-center justify-center gap-3 px-4 py-4">
          <i className="ri-exchange-dollar-line text-foreground-300 text-sm" />
          <span className="text-xs text-foreground-400">Kur bilgisi alınamadı</span>
          <button
            onClick={handleRefresh}
            className="text-xs text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
          >
            Yeniden Dene
          </button>
        </div>
      )}

      {(rates || (loading && rates)) && (
        <div className="flex items-stretch overflow-x-auto scrollbar-thin">
          {CURRENCIES.map((cur) => (
            <RateBar key={cur.key} currency={cur} rates={rates} />
          ))}
        </div>
      )}
    </div>
  );
}